import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Post } from '../models/post';
import { Comment } from '../models/comment.js';
import { IGenericJSON } from '../helpers/interface';
import { WebsocketService } from './websocket.service';

@Injectable({ providedIn: 'root' })
export class StudentPostsService {
  private http: HttpClient = inject(HttpClient);
  private wsService: WebsocketService = inject(WebsocketService);

  constructor() {}

  /**
   * Emits whenever a post (or one of its comments/replies) is created,
   * updated, or deleted anywhere, so an open feed can refetch and stay
   * live. The payload is deliberately just a signal (post_id) rather than
   * the post itself — visibility filtering (grade level, counselor
   * linkage, interests) already lives in getPosts() on the server, and
   * duplicating it into the socket broadcast would mean maintaining two
   * copies of that logic.
   * @returns {Observable<{ post_id: number }>}
   */
  onPostChanged(): Observable<{ post_id: number }> {
    return this.wsService.on<{ post_id: number }>('post:changed');
  }

  /**
   * Fetches a paginated list of student feed posts.
   * @param {number} limit - Maximum number of posts to return. Defaults to 10.
   * @param {number} offset - Number of posts to skip for pagination. Defaults to 0.
   * @param {string} q - Optional search query, matched against post content.
   * @param {number[]} careerIds - Optional career filter chip selection.
   * @param {number[]} interestIds - Optional interest filter chip selection.
   * @returns {Observable<Post[]>}
   */
  getPosts(
    limit = 10,
    offset = 0,
    q = '',
    careerIds: number[] = [],
    interestIds: number[] = [],
  ): Observable<Post[]> {
    let url = `/api/studentPosts?limit=${limit}&offset=${offset}`;
    if (q.trim()) {
      url += `&q=${encodeURIComponent(q)}`;
    }
    if (careerIds.length) {
      url += `&careerIds=${careerIds.join(',')}`;
    }
    if (interestIds.length) {
      url += `&interestIds=${interestIds.join(',')}`;
    }
    return this.http.get<IGenericJSON[]>(url).pipe(map((data) => Post.digestResponse(data)));
  }

  /**
   * Creates a post authored by the current student. Requires the
   * `posts.create` permission server-side.
   * @param {{ content: string; careers?: number[]; interests?: number[] }} payload
   * @returns {Observable<any>}
   */
  createPost(payload: {
    content: string;
    careers?: number[];
    interests?: number[];
  }): Observable<any> {
    return this.http.post(`/api/studentPosts`, payload);
  }

  /**
   * Fetches a paginated list of the current student's own posts, newest first.
   * Requires the `posts.view` permission server-side.
   * @param {number} limit - Maximum number of posts to return. Defaults to 10.
   * @param {number} offset - Number of posts to skip for pagination. Defaults to 0.
   * @param {string} q - Optional search query, matched against post content.
   * @param {number[]} careerIds - Optional career filter chip selection.
   * @param {number[]} interestIds - Optional interest filter chip selection.
   * @returns {Observable<Post[]>}
   */
  getMyPosts(
    limit = 10,
    offset = 0,
    q = '',
    careerIds: number[] = [],
    interestIds: number[] = [],
  ): Observable<Post[]> {
    let url = `/api/studentPosts/mine?limit=${limit}&offset=${offset}`;
    if (q.trim()) {
      url += `&q=${encodeURIComponent(q)}`;
    }
    if (careerIds.length) {
      url += `&careerIds=${careerIds.join(',')}`;
    }
    if (interestIds.length) {
      url += `&interestIds=${interestIds.join(',')}`;
    }
    return this.http.get<IGenericJSON[]>(url).pipe(map((data) => Post.digestResponse(data)));
  }

  /**
   * Fetches paginated comments for a given post, including nested replies.
   * @param {any} post_id - The ID of the post to fetch comments for.
   * @param {number} limit - Maximum number of comments to return. Defaults to 10.
   * @param {number} offset - Number of comments to skip for pagination. Defaults to 0.
   * @returns {Observable<Comment[]>}
   */
  getComments(post_id: any, limit = 10, offset = 0): Observable<Comment[]> {
    return this.http
      .get<IGenericJSON[]>(`/api/studentPosts/comments`, {
        params: {
          post_id,
          limit,
          offset,
        },
      })
      .pipe(map((data) => Comment.digestResponse(data)));
  }

  /**
   * Creates a new comment on a post, optionally as a reply to an existing comment.
   * @param {number} postId - The ID of the post to comment on.
   * @param {string} content - The text content of the comment.
   * @param {number} [parent_id] - The ID of the parent comment if this is a reply.
   * @returns {Observable<any>}
   */
  createComment(postId: number, content: string, parent_id?: number): Observable<any> {
    return this.http.post(`/api/studentPosts/comments`, {
      post_id: postId,
      content,
      parent_id: parent_id || null,
    });
  }

  /**
   * Fetches all posts the current user has hidden.
   * @returns {Observable<Post[]>}
   */
  getHiddenPosts(): Observable<Post[]> {
    return this.http
      .get<IGenericJSON[]>(`/api/studentPosts/hidden`)
      .pipe(map((data) => Post.digestResponse(data)));
  }

  /**
   * Hides a post from the current user's feed.
   * @param {number} postId - The ID of the post to hide.
   * @returns {Observable<any>}
   */
  hidePost(postId: number): Observable<any> {
    return this.http.post(`/api/studentPosts/hide`, { post_id: postId });
  }

  /**
   * Unhides a post from the current user's feed.
   * @param {number} postId - The ID of the post to unhide.
   * @returns {Observable<any>}
   */
  unhidePost(postId: number): Observable<any> {
    return this.http.delete(`/api/studentPosts/hide/${postId}`);
  }

  /**
   * Hides a comment from the current user's view.
   * @param {number} commentId - The ID of the comment to hide.
   * @returns {Observable<any>}
   */
  hideComment(commentId: number): Observable<any> {
    return this.http.post(`/api/studentPosts/comments/hide`, { comment_id: commentId });
  }

  /**
   * Unhides a comment from the current user's view.
   * @param {number} commentId - The ID of the comment to unhide.
   * @returns {Observable<any>}
   */
  unhideComment(commentId: number): Observable<any> {
    return this.http.delete(`/api/studentPosts/comments/hide/${commentId}`);
  }
}
